/* eslint-disable no-nested-ternary */
import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from 'components/LoadingSpinner';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import { Context, initialState } from 'components/Store';
import { SEARCH_TEXT_VALUE_FFT, SET_PROPERTY_FILTERS } from 'reducers/reducer';
import usePropertyFilters from 'hooks/usePropertyFilters';
import NoSearchResultsIcon from 'assets/no_search_results_found.svg';
import { filterPropertiesBySearchText } from '../helpers/propertyFilters';
import {
  EmptyProperties,
  PanelHeader,
  PropertyTreeView
} from '../components/PanelComponents';
import PropertyFilters from '../components/PropertyTreeView/components/PropertyFilters';
import PanelFooter from '../components/PanelFooter';
import PropertyLandingContext from '../context';

const propertiesTreeDefaultPropProperties = [];
const propertiesTreeDefaultPropRawProperties = [];
const emptyStateButtonStyle = {
  minWidth: 103,
  height: 'auto',
  padding: '10px 16px',
  lineHeight: '20px'
};
const PropertiesTree = ({
  properties = propertiesTreeDefaultPropProperties,
  rawProperties = propertiesTreeDefaultPropRawProperties,
  openCreateModal,
  memberRole,
  reloadProperties = null
}) => {
  const {
    isLoadingProperties,
    viewMode,
    financialAccess,
    fetchProperties,
    onViewModeChange,
    isHugeOrganization,
    landingResults,
    guardNavigation
  } = useContext(PropertyLandingContext);
  const [
    { searchTextValueFFT, isMassAssignerActive, isExportPDFMapsActive },
    dispatch
  ] = useContext(Context);
  const isSelectionFlow = isMassAssignerActive || isExportPDFMapsActive;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { hasActiveFilters } = usePropertyFilters();

  const onOpenFilters = () => setFiltersOpen(true);
  const onCloseFilters = () => setFiltersOpen(false);
  const searchValue = searchTextValueFFT?.valueInput || '';
  const hasSearch = !!searchValue.trim();
  const searchFiltered =
    landingResults?.searchResults ??
    (isHugeOrganization
      ? properties
      : filterPropertiesBySearchText(properties, searchValue));
  const showNoResults =
    !isSelectionFlow &&
    !isLoadingProperties &&
    !searchFiltered?.length &&
    (hasSearch || hasActiveFilters() || rawProperties.length > 0);
  const showOrgEmpty =
    !isSelectionFlow &&
    !isLoadingProperties &&
    !searchFiltered?.length &&
    !hasSearch &&
    !hasActiveFilters() &&
    !rawProperties.length;

  const resetSelections = () => {
    dispatch({
      type: SET_PROPERTY_FILTERS,
      payload: {
        showFarmWithIds: [],
        showArchivedProperties: false,
        cropZoneFilters: {
          ...initialState.propertyFilters.cropZoneFilters
        }
      }
    });
    dispatch({
      type: SEARCH_TEXT_VALUE_FFT,
      payload: { valueInput: '' }
    });
  };
  const onClearSelections = () => guardNavigation(resetSelections);

  return (
    <div
      data-testid="property-landing-property-tree"
      className="h-full bg-white cursor-default flex flex-col outline-r outline-neutral-20"
    >
      {filtersOpen ? (
        <PropertyFilters properties={rawProperties} onClose={onCloseFilters} />
      ) : (
        <>
          <PanelHeader
            showTools
            title="Properties"
            reloadFarmData={fetchProperties}
            openCreateModal={openCreateModal}
            openFilters={onOpenFilters}
            loading={isLoadingProperties}
            onChangeView={onViewModeChange}
            view={viewMode}
          />

          {isLoadingProperties && (
            <LoadingSpinner message="" label="Loading properties" />
          )}
          {!isLoadingProperties &&
            (isSelectionFlow || searchFiltered?.length > 0) && (
              <PropertyTreeView
                properties={properties}
                searchResults={searchFiltered}
              />
            )}
          {showNoResults && (
            <div className="flex flex-1 flex-col items-center justify-center px-5 py-8 text-center">
              <output className="sr-only">
                No Properties Found. Please try a different search or filter.
              </output>
              <img
                src={NoSearchResultsIcon}
                alt=""
                width={234}
                height={160}
                className="-translate-y-2"
              />
              <h2 className="mt-2 text-sm font-semibold leading-5">
                No Properties Found
              </h2>
              <p className="mt-2 text-sm leading-5 text-neutral-60">
                Please try a different search or filter.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {hasActiveFilters() && (
                  <Button
                    type={ButtonType.primary}
                    size="small"
                    style={emptyStateButtonStyle}
                    onClick={onOpenFilters}
                  >
                    Adjust Filters
                  </Button>
                )}
                <Button
                  type={ButtonType.outline}
                  size="small"
                  style={emptyStateButtonStyle}
                  onClick={onClearSelections}
                >
                  Clear Selections
                </Button>
              </div>
            </div>
          )}
          {showOrgEmpty && (
            <EmptyProperties
              openCreateModal={openCreateModal}
              memberRole={memberRole}
              memberFinancialAccess={financialAccess}
            />
          )}

          <PanelFooter
            showTools={!!searchFiltered?.length || showNoResults}
            reloadFarmData={reloadProperties ?? fetchProperties}
            openCreateModal={openCreateModal}
            setView={onViewModeChange}
          />
        </>
      )}
    </div>
  );
};

PropertiesTree.propTypes = {
  properties: PropTypes.arrayOf(PropTypes.object),
  rawProperties: PropTypes.arrayOf(PropTypes.object),
  openCreateModal: PropTypes.func.isRequired,
  memberRole: PropTypes.string.isRequired,
  reloadProperties: PropTypes.func
};

export default PropertiesTree;
